const AI_CLASS_THRESHOLD = 0.2

const MULTIPLE_HEADER_CLASSES = ['c3', 'c4', 'c5']

const AlterationScoreCSSClass = Object.freeze({
  NO_INFO: 'no-info',
  GREEN: 'ai-green',
  YELLOW: 'ai-yellow',
  RED: 'ai-red'
})

class AIService {

  // Use those threshold for mcRAS or DRAS models
  static LOW_THRESHOLD = 0.2
  static HIGH_THRESHOLD = 0.7

  static MCRASE2_DR_LOW_THRESHOLD = 0.004
  static MCRASE2_DR_HIGH_THRESHOLD = 0.2
  static MCRASE2_OTHER_CLASSES_LOW_THRESHOLD = 0.2
  static MCRASE2_OTHER_CLASSES_HIGH_THRESHOLD = 0.7

  mcRASLabels = {
    0: 'clinicConfig.mcRAS_noAlt',
    1: 'clinicConfig.mcRAS_c1',
    2: 'clinicConfig.mcRAS_c2',
    3: 'clinicConfig.mcRAS_c3',
    4: 'clinicConfig.mcRAS_c5',
    5: 'clinicConfig.mcRAS_c6'
  }

  mcRASE2DRLabels = 'mcRASE2_c1'
  mcRASE2OtherDiseasesLabels = {
    0: 'mcRASE2_c2',
    1: 'mcRASE2_c3',
    2: 'mcRASE2_c4',
    3: 'mcRASE2_c5'
  }

  mcRASHeader(exam) {
    const aiClasses = this.mcRASClasses(exam)

    if (aiClasses.length == 0)
      return ""

    if (aiClasses.length > 1)
      return "multiple"

    const singleClass = aiClasses[0]
    if (MULTIPLE_HEADER_CLASSES.includes(singleClass))
      return "multiple" // "Suspeita de uma das seguintes condições:"

    return "single"
  }

  mcRASClasses(exam) {
    let result = []
    try {
      if (!exam.metadata)
        return []

      let data = JSON.parse(exam.metadata)

      for (let i in data.mcRAS) {
        if (i == 0) // ignoring first class (normal)
          continue

        let value = data.mcRAS[i]
        if (i == 1) {
          if (value >= 0.004) {
            result.push(`c${i}`)
          }
          continue
        }

        if (value >= AI_CLASS_THRESHOLD) {
          result.push(`c${i}`)
        }
      }
    } catch (err) {
      console.error("Error parsing metadata.", err)
    }

    return result
  }

  getModelVersion(exam) {
    if (!exam.metadata)
      return false
    const metadata = JSON.parse(exam.metadata)

    return metadata.modelVersion
  }

  getMCRAS(exam) {
    const key = 'mcRAS'
    if (!exam.metadata)
      return undefined
    const metadata = JSON.parse(exam.metadata)
    return metadata[key]
  }

  getMaxAlterationQualityClassMCRAS(exam, laterality = undefined) {
    let key = 'maxAlterationQualityMCRAS'
    if (laterality)
      key += laterality

    const metadata = JSON.parse(exam.metadata)
    return metadata[key]
  }


  hasMCRAS(exam) {
    if (!exam.metadata)
      return false
    const metadata = JSON.parse(exam.metadata)

    return metadata.mcRASE2 || metadata.mcRASE2OD || metadata.mcRASE2OS
  }

  hasAnyAlterationScoreInfo(exam) {
    if (!exam.metadata)
      return false
    const metadata = JSON.parse(exam.metadata)
    return metadata["maxAlterationScoreOD"] !== undefined ||
      metadata["maxAlterationScoreOS"] !== undefined ||
      metadata['mcRAS'] !== undefined ||
      metadata['mcRASE2OD'] !== undefined ||
      metadata['mcRASE2OS'] !== undefined
  }

  hasAlterationInformationByEye(exam) {
    if (!exam.metadata)
      return false
    const metadata = JSON.parse(exam.metadata)
    return metadata["maxAlterationScoreOD"] !== undefined ||
      metadata["maxAlterationScoreOS"] !== undefined ||
      metadata['mcRASE2OD'] !== undefined ||
      metadata['mcRASE2OS'] !== undefined
  }

  isAlterationInformationE2MultiClass(exam) {
    if (!exam.metadata)
      return false
    const metadata = JSON.parse(exam.metadata)
    return metadata['mcRASE2OD'] !== undefined ||
      metadata['mcRASE2OS'] !== undefined
  }

  getMCRASE2(exam, laterality) {
    let key = 'mcRASE2'
    if (laterality)
      key += laterality

    const metadata = JSON.parse(exam.metadata)
    return metadata[key]
  }

  getMaxAlterationScore(exam, laterality) {
    let key = 'maxAlterationScore'
    if (laterality)
      key += laterality
    if (!exam.metadata)
      return undefined
    const metadata = JSON.parse(exam.metadata)
    return metadata[key]
  }

  getMCRASE2AlterationScore(exam, laterality) {
    let key = 'mcRASE2'
    if (laterality)
      key += laterality

    const metadata = JSON.parse(exam.metadata)
    return metadata[key]
  }

  /**
   * Get the alteration score CSS class for the array of scores of mcRAS
   * @param alterationScores Array of all alteration scores, with the class 0
   * @returns 
   */
  getmcRASAlterationScoreCSSClass(alterationScores) {
    if (!alterationScores)
      return AlterationScoreCSSClass.NO_INFO
    // Ignore the first item of array (No alteration class)
    let alterationClasses = alterationScores.slice(1) // Removing No Alteration Class
    const maxAnyAlteration = Math.max(...alterationClasses)
    const hasAnyAlteration = alterationClasses.some(score => score >= AIService.LOW_THRESHOLD)

    if (!hasAnyAlteration) {
      return AlterationScoreCSSClass.GREEN
    }

    return maxAnyAlteration > AIService.HIGH_THRESHOLD ? AlterationScoreCSSClass.RED : AlterationScoreCSSClass.YELLOW
  }

  getmcRASE2AlterationScoreCSSClass(alterationScores) {
    if (!alterationScores || alterationScores.every(score => score === 0))
      return AlterationScoreCSSClass.NO_INFO
    // Ignore the first item of array (No alteration class)
    const drAlterationScore = alterationScores[1]
    let alterationClasses = alterationScores.slice(2) // Removing No Alteration and DR class
    const maxAnyAlteration = Math.max(...alterationClasses)
    const hasAnyAlteration = alterationClasses.some(score => score >= AIService.MCRASE2_OTHER_CLASSES_LOW_THRESHOLD) || drAlterationScore > AIService.MCRASE2_DR_LOW_THRESHOLD

    if (!hasAnyAlteration) {
      return AlterationScoreCSSClass.GREEN
    }
    let returnClass = AlterationScoreCSSClass.GREEN
    if (drAlterationScore > AIService.MCRASE2_DR_LOW_THRESHOLD) {
      returnClass = drAlterationScore > AIService.MCRASE2_DR_HIGH_THRESHOLD ? AlterationScoreCSSClass.RED : AlterationScoreCSSClass.YELLOW
    }
    if (returnClass === AlterationScoreCSSClass.RED) {
      return returnClass
    }

    return maxAnyAlteration > AIService.MCRASE2_OTHER_CLASSES_HIGH_THRESHOLD ? AlterationScoreCSSClass.RED : AlterationScoreCSSClass.YELLOW
  }

  shouldShowMultiClassEntry(odClass, osClass) {
    const yellowOrRedClasses = [AlterationScoreCSSClass.YELLOW, AlterationScoreCSSClass.RED];
    // Check if either class is in the significant set
    return yellowOrRedClasses.includes(odClass) || yellowOrRedClasses.includes(osClass);
  }

  processMCRASE2Score(score, klass) {
    if (score === 'LQ' || score === undefined || typeof score !== 'number' || score === 0) {
      return AlterationScoreCSSClass.NO_INFO
    }
    // This will only run if all other classes doesnt meet their threshold
    if (klass === 0 && score > AIService.LOW_THRESHOLD) {
      return AlterationScoreCSSClass.GREEN
    }

    if (klass === 1) {
      if (score < AIService.MCRASE2_DR_LOW_THRESHOLD) {
        return AlterationScoreCSSClass.GREEN
      } else if (score < AIService.MCRASE2_DR_HIGH_THRESHOLD) {
        return AlterationScoreCSSClass.YELLOW
      }
      return AlterationScoreCSSClass.RED
    }

    if (score >= AIService.LOW_THRESHOLD && score < AIService.HIGH_THRESHOLD) {
      return AlterationScoreCSSClass.YELLOW
    }
    if (score >= AIService.HIGH_THRESHOLD)
      return AlterationScoreCSSClass.RED
    return AlterationScoreCSSClass.GREEN
  }

  getMCRASE2AlterationText(mcRASOD, mcRASOS) {
    let maxAlterationArray = []
    if (mcRASOD && mcRASOS) {
      if (mcRASOD.length !== mcRASOS.length) {
        throw Error(`[AIService] Different alteration array length`)
      }
      // Get the greatest alteration value for each class
      for (let i = 0; i < mcRASOD.length; ++i) {
        maxAlterationArray.push(mcRASOD[i] > mcRASOS[i] ? mcRASOD[i] : mcRASOS[i])
      }
    } else {
      maxAlterationArray = mcRASOD ? mcRASOD : mcRASOS
    }

    let alterationsText = []

    const drAlterationScore = maxAlterationArray[1]
    let alterationClasses = maxAlterationArray.slice(2) // Removing No Alteration and DR class

    if (drAlterationScore > AIService.MCRASE2_DR_LOW_THRESHOLD) {
      alterationsText.push(this.mcRASE2DRLabels)
    }
    for (let i = 0; i < alterationClasses.length; ++i) {
      let alteration = alterationClasses[i]
      if (alteration > AIService.MCRASE2_OTHER_CLASSES_LOW_THRESHOLD) {
        alterationsText.push(this.mcRASE2OtherDiseasesLabels[i])
      }
    }
    return alterationsText
  }

  processAlterationScore(score) {
    if (score === 'LQ' || score === undefined || typeof score !== 'number') {
      return AlterationScoreCSSClass.NO_INFO
    }
    if (score < AIService.LOW_THRESHOLD) {
      return AlterationScoreCSSClass.GREEN
    }
    return score > AIService.HIGH_THRESHOLD ? AlterationScoreCSSClass.RED : AlterationScoreCSSClass.YELLOW
  }

  insertScoreResultInto(exam, showMultiClass) {
    const hasAnyAlterationScoreInfo = this.hasAnyAlterationScoreInfo(exam)
    if (!hasAnyAlterationScoreInfo) {
      return
    }
    const showDoubleAlterationCircle = this.hasAlterationInformationByEye(exam)
    if (showDoubleAlterationCircle) {
      const isAlterationInformationMultiClass = this.isAlterationInformationE2MultiClass(exam)
      if (isAlterationInformationMultiClass) {
        const mcRASVersion = this.getModelVersion(exam)
        if (mcRASVersion !== 'mcRASE2_1.0') {
          // Up to now, the only version known is mcRASE2_1.0
          return
        }

        const mcRASOD = this.getMCRASE2(exam, 'OD')
        const mcRASOS = this.getMCRASE2(exam, 'OS')

        const odClass = this.getmcRASE2AlterationScoreCSSClass(mcRASOD)
        const osClass = this.getmcRASE2AlterationScoreCSSClass(mcRASOS)
        const scoreResult = { odStatus: odClass, osStatus: osClass, doubleCircle: true }
        exam["scoreResult"] = scoreResult

        if (showMultiClass) {
          exam["scoreResult"]["alterationText"] = this.getMCRASE2AlterationText(mcRASOD, mcRASOS)
        }
        return
      }
      /*
      If the exam has some alteration score information, its by eye and its not multiclass, 
              it should be using the 'maxAlterationScore[OD|OS]' format
      */
      const alterationScoreOD = this.getMaxAlterationScore(exam, 'OD')
      const alterationScoreOS = this.getMaxAlterationScore(exam, 'OS')
      const odClass = this.processAlterationScore(alterationScoreOD)
      const osClass = this.processAlterationScore(alterationScoreOS)
      const scoreResult = { odStatus: odClass, osStatus: osClass, doubleCircle: true }
      exam["scoreResult"] = scoreResult
      return
    }
    const mcRASScores = this.getMCRAS(exam)
    const singleAlterationCircle = this.getmcRASAlterationScoreCSSClass(mcRASScores)
    const scoreResult = { odStatus: singleAlterationCircle, doubleCircle: false }
    exam["scoreResult"] = scoreResult
  }
}

const aiService = new AIService()
export default aiService
